import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { PageShell } from "@/components/Navbar";
import { PageBanner, PAGE_BANNERS } from "@/components/PageBanner";
import { MyNetworkTab } from "@/components/RelationshipNetwork";
import AdPlacement from "@/components/AdPlacement";
import { FeaturedProfessionalSpotlight } from "@/components/FeaturedProfessionalSpotlight";
import { trackFeatured } from "@/lib/featuredPro";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/track";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { publicSiteUrl, copyToClipboard, canNativeShare, nativeShare } from "@/lib/share";
import { ReferClientModal } from "@/components/ReferClientModal";
import { readReferralResume, clearReferralResume } from "@/lib/referralResume";
import {
  Search, UserPlus, Check, X, Clock, BadgeCheck, Users, Inbox, Send, History,
  Building2, MapPin, Loader2, Handshake, RotateCcw, Sparkles, Link2, Share2, MessageCircle,
  Mail, MessageSquare, MessagesSquare, ShieldCheck, UserRound,
} from "lucide-react";

const ORG_GROUP_OPTIONS = [
  { value: "all", label: "All" },
  { value: "developers", label: "Developers" },
  { value: "brokerages", label: "Brokerages" },
  { value: "property_management", label: "Property Management" },
  { value: "banks_financing", label: "Banks & Financing" },
  { value: "professional_services", label: "Professional Services" },
  { value: "associations", label: "Associations" },
];

const ROLE_OPTIONS = [
  { value: "any", label: "All roles" },
  { value: "LICENSED_BROKER", label: "Licensed Broker" },
  { value: "SALESPERSON", label: "Salesperson" },
  { value: "PROPERTY_MANAGER", label: "Property Manager" },
  { value: "PROPERTY_OWNER", label: "Property Owner" },
  { value: "DEVELOPER_REP", label: "Developer Rep" },
  { value: "OTHER", label: "Other" },
];
const TABS = ["discover", "connections", "requests", "interactions"];

// Non-PII referral attribution — first-touch UTM captured by the analytics tracker when a
// recipient (guest-friendly) opens the link. LEGACY analytics key: utm_source stays
// 'colleague_invite' even though the UI is now "Invite a Professional" — renaming it would
// fragment existing Production reporting, so it is retained as a documented legacy key.
const INVITE_URL = `${publicSiteUrl()}?utm_source=colleague_invite&utm_medium=referral&utm_campaign=connections`;
const INVITE_SUBJECT = "Join me on PropertyConnect";
const INVITE_MESSAGE = "Join me on PropertyConnect—a professional real-estate network where we can connect with verified professionals, discover properties and Buyer Needs, and collaborate on opportunities.\n\nExpand your network. Discover more opportunities.";
// Full share payload always includes the ATTRIBUTED url (not just the bare domain).
const INVITE_SHARE_TEXT = `${INVITE_MESSAGE}\n\n${INVITE_URL}`;
const INVITE_SMS_TEXT = `${INVITE_MESSAGE.replace(/\n+/g, " ")} ${INVITE_URL}`;

const initials = (n) => (n || "P").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

function Avatar({ name, url, size = "h-11 w-11" }) {
  return url ? (
    <img src={url} alt="" className={`${size} rounded-full object-cover bg-slate-100 shrink-0`} />
  ) : (
    <span className={`${size} rounded-full bg-[#0F2C59]/10 text-[#0F2C59] font-semibold flex items-center justify-center shrink-0 text-sm`}>{initials(name)}</span>
  );
}

function MetaRow({ p }) {
  // Hierarchy: Professional Role / Sales Position · Organization / Location.
  // Omit missing fields; never render dangling separators or empty rows.
  const posOrg = [p.sales_position_label, p.organization].filter(Boolean).join(" · ");
  return (
    <div className="mt-1 space-y-0.5 text-[13px] text-slate-500 min-w-0">
      {p.role_label && <div className="flex items-center gap-1.5 text-slate-600 font-medium min-w-0"><Handshake className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.role_label}</span></div>}
      {posOrg && <div className="flex items-center gap-1.5 min-w-0"><Building2 className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{posOrg}</span></div>}
      {p.location && <div className="flex items-center gap-1.5 min-w-0"><MapPin className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{p.location}</span></div>}
    </div>
  );
}

function EmptyState({ icon: Icon, title, hint, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 py-14 px-6 text-center">
      <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-[#0F2C59]/8 text-[#0F2C59]"><Icon className="h-6 w-6" /></span>
      <p className="font-display text-lg font-semibold text-[#0F2C59]">{title}</p>
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">{hint}</p>
    </div>
  );
}

export default function Connections() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab = TABS.includes(rawTab) ? rawTab : "discover";

  const [busy, setBusy] = useState({});
  const [summary, setSummary] = useState({ incoming_pending: 0, connections: 0 });
  const [drawerId, setDrawerId] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  // When the dedicated Featured Professional Spotlight is showing in Discover→Professionals,
  // suppress the generic CONNECTIONS_TOP house/sponsored strip so we don't stack two ad
  // surfaces before organic. Falls back to CONNECTIONS_TOP whenever there is no inventory.
  const [proSpotlightActive, setProSpotlightActive] = useState(false);
  const navigate = useNavigate();

  const openConversation = async (proId) => {
    try {
      const r = await api.post("/messages/conversations", { recipient_id: proId });
      navigate(`/messages?conversation=${r.data.conversation_id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const setTab = (t) => setParams(t === "discover" ? {} : { tab: t }, { replace: true });
  const mark = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  useEffect(() => { trackEvent("connections_page_viewed", { path: "/connections" }); }, []);

  // CP5 referral modal (page-level; recipient preselected from a connection or resume).
  const [refer, setRefer] = useState({ open: false, recipient: null, needId: null });
  const openRefer = (p) => setRefer({ open: true, needId: null, recipient: { id: p.id, name: p.name, avatar: p.avatar_url, role_label: p.role_label, organization_name: p.organization_name, city: p.city } });
  useEffect(() => {
    const r = readReferralResume();
    if (r?.recipient) { setRefer({ open: true, recipient: r.recipient, needId: r.needId || null }); clearReferralResume(); }
  }, []);

  const loadSummary = useCallback(() => {
    api.get("/connections/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary, tab]);

  return (
    <PageShell testid="connections-page">
      <PageBanner
        testid="connections-banner"
        image={PAGE_BANNERS.connections}
        icon={Users}
        title="Connections"
        description="Build and manage the people and organizations behind your real estate opportunities."
        actions={
          <Button data-testid="header-invite-btn" onClick={() => setInviteOpen(true)} variant="outline" className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5 shrink-0 self-start">
            <UserPlus className="h-4 w-4 mr-1.5" /> Invite a Professional
          </Button>
        }
      />

      {!(tab === "discover" && proSpotlightActive) && (
        <AdPlacement placement="CONNECTIONS_TOP" variant="strip" className="mb-6" />
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="connections-tabs" className="mb-6 flex flex-wrap h-auto gap-1 bg-slate-100/80 p-1">
          <TabsTrigger value="discover" data-testid="tab-discover" className="gap-1.5"><Search className="h-4 w-4" /> Discover</TabsTrigger>
          <TabsTrigger value="connections" data-testid="tab-connections" className="gap-1.5"><Users className="h-4 w-4" /> My Network</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="gap-1.5">
            <Inbox className="h-4 w-4" /> Requests
            {summary.incoming_pending > 0 && (
              <span data-testid="requests-badge" className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C79A3B] text-[#0F2C59] text-[10px] font-bold flex items-center justify-center">{summary.incoming_pending > 99 ? "99+" : summary.incoming_pending}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="interactions" data-testid="tab-interactions" className="gap-1.5"><History className="h-4 w-4" /> Recent</TabsTrigger>
        </TabsList>

        <TabsContent value="discover"><DiscoverTab busy={busy} mark={mark} onChanged={loadSummary} openProfile={setDrawerId} onInvite={() => setInviteOpen(true)} onSpotlightChange={setProSpotlightActive} /></TabsContent>
        <TabsContent value="connections"><MyNetworkTab renderProfessionals={() => <ConnectionsTab openProfile={setDrawerId} onMessage={openConversation} onRefer={openRefer} />} /></TabsContent>
        <TabsContent value="requests"><RequestsTab busy={busy} mark={mark} onChanged={loadSummary} openProfile={setDrawerId} /></TabsContent>
        <TabsContent value="interactions"><InteractionsTab /></TabsContent>
      </Tabs>

      <ProfileDrawer id={drawerId} onClose={() => setDrawerId(null)} busy={busy} mark={mark} onChanged={loadSummary} onMessage={openConversation} />
      <InviteProfessionalDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ReferClientModal recipient={refer.recipient} initialNeedId={refer.needId} open={refer.open} entrySurface="CONNECTIONS"
        onOpenChange={(o) => setRefer((s) => ({ ...s, open: o }))}
        onCreated={() => { setRefer({ open: false, recipient: null, needId: null }); navigate("/referrals?tab=sent"); }} />
    </PageShell>
  );
}

/* ---------------- Discover ---------------- */
// Name/photo link to the published public profile (/pro/:slug) when available;
// otherwise falls back to the in-app professional preview drawer. Never exposes private data.
function ProfileHead({ p, onFallback, testid, className = "flex items-start gap-3 text-left w-full min-w-0", children }) {
  if (p.public_profile_path) {
    return <Link to={p.public_profile_path} data-testid={testid} className={className}>{children}</Link>;
  }
  return <button onClick={onFallback} data-testid={testid} className={className}>{children}</button>;
}

// Explicit, always-available "View Profile" action (independent of connection status).
function ViewProfile({ p, testid }) {
  if (!p.public_profile_path) {
    return <span data-testid={`${testid}-unavailable`} className="text-[11px] text-slate-400 whitespace-nowrap">Public profile unavailable</span>;
  }
  return (
    <Link to={p.public_profile_path} data-testid={testid}
      className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#0F2C59] hover:text-[#C79A3B] whitespace-nowrap">
      <UserRound className="h-4 w-4" /> View Profile
    </Link>
  );
}

function DiscoverTab({ busy, mark, onChanged, openProfile, onInvite, onSpotlightChange }) {
  const { config } = useAuth();
  const orgsEnabled = !!config?.organizations_enabled;
  const [subMode, setSubMode] = useState("professionals");
  const [spotlightHasInv, setSpotlightHasInv] = useState(false);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("any");
  const [city, setCity] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 20;

  // Reset to page 1 whenever the search, role or location filter changes.
  useEffect(() => { setPage(1); }, [q, role, city]);

  // Tell the page to suppress CONNECTIONS_TOP only when the Professionals spotlight is live.
  useEffect(() => {
    onSpotlightChange?.(subMode === "professionals" && spotlightHasInv);
  }, [subMode, spotlightHasInv]); // eslint-disable-line

  const load = useCallback(() => {
    setLoading(true);
    const p = { limit: PAGE_SIZE, page };
    if (q.trim()) p.q = q.trim();
    if (role !== "any") p.role = role;
    if (city.trim()) p.city = city.trim();
    api.get("/connections/discover", { params: p })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [q, role, city, page]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {orgsEnabled && (
        <div className="mb-4 inline-flex rounded-xl bg-slate-100 p-1" data-testid="discover-submode-toggle">
          <button data-testid="discover-submode-professionals" onClick={() => setSubMode("professionals")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${subMode === "professionals" ? "bg-white text-[#0F2C59] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Users className="h-4 w-4" /> Professionals
          </button>
          <button data-testid="discover-submode-organizations" onClick={() => setSubMode("organizations")}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${subMode === "organizations" ? "bg-white text-[#0F2C59] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            <Building2 className="h-4 w-4" /> Organizations
          </button>
        </div>
      )}

      {orgsEnabled && subMode === "organizations" ? (
        <OrganizationsDiscover />
      ) : (
      <div>
      <FeaturedProfessionalSpotlight onInventory={setSpotlightHasInv} filters={{ q, role, city }}
        renderAction={(c) => <SpotlightConnect c={c} busy={busy} mark={mark} onChanged={onChanged} />} />
      <div className="mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="discover-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or organization…" className="pl-9 rounded-xl" />
        </div>
        <div className="relative sm:w-48">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="discover-city-filter" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City or location" className="pl-9 rounded-xl" />
        </div>
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger data-testid="discover-role-filter" className="sm:w-56 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {spotlightHasInv && <h3 data-testid="discover-directory-heading" className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Directory results</h3>}

      {loading ? (
        <div data-testid="discover-loading" className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState testid="discover-empty" icon={Sparkles}
          title={q || role !== "any" ? "No professionals match your filters" : "The verified network is growing"}
          hint={q || role !== "any" ? "Try clearing the search or choosing a different role — only verified professionals appear here." : "Only verified real-estate professionals appear in Discover. Check back soon as more professionals get verified."} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((p) => (
            <div key={p.id} data-testid={`discover-card-${p.id}`} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow">
              <ProfileHead p={p} onFallback={() => openProfile(p.id)} testid={`discover-open-${p.id}`}>
                <Avatar name={p.name} url={p.avatar_url} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="font-semibold text-[#0F2C59] truncate">{p.name}</span>
                    {p.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}
                  </div>
                  <MetaRow p={p} />
                </div>
              </ProfileHead>
              <div className="mt-3 flex items-center justify-between gap-2">
                <ViewProfile p={p} testid={`discover-view-profile-${p.id}`} />
                <ConnectButton p={p} busy={busy} mark={mark} onChanged={onChanged} />
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div data-testid="discover-pagination" className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" data-testid="discover-prev" disabled={page <= 1} onClick={() => setPage((n) => Math.max(1, n - 1))} className="rounded-xl">Previous</Button>
          <span data-testid="discover-page-indicator" className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" data-testid="discover-next" disabled={page >= totalPages} onClick={() => setPage((n) => Math.min(totalPages, n + 1))} className="rounded-xl">Next</Button>
        </div>
      )}

      {!loading && <InvitePrompt emphasize={items.length < 3} onOpen={onInvite} />}
      </div>
      )}
    </div>
  );
}

/* ---------------- Organizations discovery (P1) ---------------- */
function OrganizationsDiscover() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const PAGE_SIZE = 24;

  useEffect(() => { setPage(1); }, [q, type]);

  const load = useCallback(() => {
    setLoading(true);
    const p = { page, page_size: PAGE_SIZE };
    if (q.trim()) p.q = q.trim();
    if (type !== "all") p.group = type;
    api.get("/public/organizations", { params: p })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [q, type, page]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="mb-5 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="org-discover-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizations by name or city…" className="pl-9 rounded-xl" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="org-discover-type-filter" className="sm:w-60 rounded-xl"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ORG_GROUP_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div data-testid="org-discover-loading" className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <EmptyState testid="org-discover-empty" icon={Building2}
          title={q || type !== "all" ? "No organizations match your filters" : "Organizations are coming online"}
          hint={q || type !== "all" ? "Try clearing the search or choosing a different group." : "Developers, brokerages, banks and real-estate service organizations will appear here as they publish their profiles."} />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((o) => (
            <Link key={o.id} to={`/org/${o.slug}`} data-testid={`org-discover-card-${o.id}`} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow flex items-start gap-3">
              <span className="h-12 w-12 rounded-xl bg-[#0F2C59]/8 flex items-center justify-center overflow-hidden shrink-0">
                {o.logo ? <img src={o.logo} alt="" className="h-full w-full object-contain" /> : <Building2 className="h-6 w-6 text-[#0F2C59]" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-semibold text-[#0F2C59] truncate">{o.name}</span>
                  {o.is_verified && <BadgeCheck className="h-4 w-4 text-emerald-600 shrink-0" />}
                  {o.is_partner && <ShieldCheck className="h-4 w-4 text-[#C79A3B] shrink-0" />}
                </div>
                <div className="mt-0.5 text-[13px] text-slate-500 truncate">{o.type_label}{o.city ? ` · ${o.city}` : ""}</div>
                {o.description && <p className="mt-1 text-[13px] text-slate-500 line-clamp-2">{o.description}</p>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div data-testid="org-discover-pagination" className="mt-6 flex items-center justify-center gap-3">
          <Button variant="outline" size="sm" data-testid="org-discover-prev" disabled={page <= 1} onClick={() => setPage((n) => Math.max(1, n - 1))} className="rounded-xl">Previous</Button>
          <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" data-testid="org-discover-next" disabled={page >= totalPages} onClick={() => setPage((n) => Math.min(totalPages, n + 1))} className="rounded-xl">Next</Button>
        </div>
      )}
    </div>
  );
}

/* ---------------- Invite a Professional (network growth) ---------------- */
function InvitePrompt({ emphasize, onOpen }) {
  return (
    <div data-testid="invite-prompt" className={`mt-6 rounded-2xl border px-5 ${emphasize ? "py-6 border-[#C79A3B]/30 bg-[#C79A3B]/[0.06]" : "py-4 border-slate-200 bg-slate-50/60"} flex flex-col sm:flex-row sm:items-center gap-4`}>
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0F2C59]/8 text-[#C79A3B] shrink-0"><UserPlus className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[#0F2C59]">Grow your professional network</p>
        <p className="text-sm text-slate-500 mt-0.5">Know a real-estate professional who should be part of the PropertyConnect network? Invite them to join and get verified.</p>
      </div>
      <Button data-testid="invite-colleague-btn" onClick={onOpen} variant="outline" className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5 shrink-0">
        <UserPlus className="h-4 w-4 mr-1.5" /> Invite a Professional
      </Button>
    </div>
  );
}

function InviteProfessionalDialog({ open, onOpenChange }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { if (open) trackEvent("colleague_invite_opened", { path: "/connections", entity_type: "invite" }); }, [open]);

  const copyLink = async (channel) => {
    trackEvent("colleague_invite_link_copied", { path: "/connections", entity_type: "invite", metadata: { channel: "copy_link" } });
    const ok = await copyToClipboard(INVITE_URL);
    if (ok) { setCopied(true); toast.success("Copied"); setTimeout(() => setCopied(false), 2000); }
    else toast.error("Could not copy the link");
    return ok;
  };

  const doShare = async () => {
    // Primary action: native Web Share where available; safe fallback to Copy Link.
    if (canNativeShare()) {
      trackEvent("colleague_invite_shared", { path: "/connections", entity_type: "invite", metadata: { channel: "native_share" } });
      const res = await nativeShare({ url: INVITE_URL, title: INVITE_SUBJECT, text: INVITE_MESSAGE });
      if (res === "unsupported") await copyLink();
    } else {
      await copyLink();
      toast.message("Sharing isn't supported on this device — invite link copied instead.");
    }
  };

  const doEmail = () => {
    trackEvent("colleague_invite_shared", { path: "/connections", entity_type: "invite", metadata: { channel: "email" } });
    window.location.href = `mailto:?subject=${encodeURIComponent(INVITE_SUBJECT)}&body=${encodeURIComponent(INVITE_SHARE_TEXT)}`;
  };

  const doSms = () => {
    trackEvent("colleague_invite_shared", { path: "/connections", entity_type: "invite", metadata: { channel: "sms" } });
    // `sms:?&body=` is the most broadly compatible prefill across iOS/Android; desktop with no
    // SMS handler simply does nothing (no error page).
    window.location.href = `sms:?&body=${encodeURIComponent(INVITE_SMS_TEXT)}`;
  };

  const doWhatsApp = () => {
    trackEvent("colleague_invite_shared", { path: "/connections", entity_type: "invite", metadata: { channel: "whatsapp" } });
    // Official endpoint: api.whatsapp.com/send redirects to the app on mobile and to
    // web.whatsapp.com on desktop. Open as a normal tab (NOT a sized popup) to avoid the
    // desktop redirect landing on a cert/privacy error. No phone number (recipient chooses).
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(INVITE_SHARE_TEXT)}`, "_blank", "noopener,noreferrer");
  };

  const Channel = ({ testid, onClick, icon: Icon, label }) => (
    <button data-testid={testid} onClick={onClick} className="min-w-0 flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 hover:border-[#0F2C59] hover:bg-slate-50 transition-colors">
      <Icon className="h-6 w-6 text-[#0F2C59] shrink-0" />
      <span className="text-xs font-semibold text-slate-700 text-center leading-tight">{label}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="invite-dialog" className="w-[calc(100vw-2rem)] max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><UserPlus className="h-5 w-5 text-[#C79A3B]" /> Invite a Professional</DialogTitle>
          <DialogDescription>Know a real-estate professional who should be part of the PropertyConnect network?</DialogDescription>
        </DialogHeader>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{INVITE_MESSAGE}</div>

        <Button data-testid="invite-share" onClick={doShare} className="w-full rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white font-semibold">
          <Share2 className="h-4 w-4 mr-2" /> Share Invitation
        </Button>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 min-w-0">
          <Channel testid="invite-copy" onClick={() => copyLink()} icon={copied ? Check : Link2} label={copied ? "Copied" : "Copy Link"} />
          <Channel testid="invite-email" onClick={doEmail} icon={Mail} label="Email" />
          <Channel testid="invite-sms" onClick={doSms} icon={MessageSquare} label="SMS/Text" />
          <Channel testid="invite-whatsapp" onClick={doWhatsApp} icon={MessageCircle} label="WhatsApp" />
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center gap-2 min-w-0">
          <span data-testid="invite-url" className="text-xs text-slate-500 truncate flex-1 min-w-0">{INVITE_URL}</span>
          <Button data-testid="invite-copy-inline" size="sm" variant="outline" onClick={() => copyLink()} className="shrink-0 rounded-lg h-8">{copied ? "Copied" : "Copy"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Connect action for a featured card: resolves the viewer's live connection state; hidden for
// self or when the professional is not connection-eligible. Successful requests are attributed.
function SpotlightConnect({ c, busy, mark, onChanged }) {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const id = c.entity?.entity_id;
  useEffect(() => {
    if (!id || id === user?.id) return;
    api.get(`/connections/professional/${id}`).then((r) => setP(r.data)).catch(() => setP(null));
  }, [id, user?.id]);
  if (!p) return null;
  return <ConnectPill p={p} busy={busy} mark={mark}
    onChanged={() => { trackFeatured("connection_request", c.id, c.placement); onChanged?.(); }} />;
}

// Compact connection status for featured cards: Connected (green) / Pending (amber) / Connect (navy).
const PILL = "inline-flex h-10 items-center gap-1 rounded-full border px-3 text-[12px] font-semibold";
function ConnectPill({ p, busy, mark, onChanged }) {
  const [state, setState] = useState(p.connection_state);
  useEffect(() => setState(p.connection_state), [p.connection_state]);
  const send = async () => {
    mark(p.id, true);
    try {
      await api.post("/connections/request", { recipient_id: p.id });
      setState("REQUEST_SENT");
      toast.success(`Connection request sent to ${p.name}`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { mark(p.id, false); }
  };
  if (state === "CONNECTED") return <span role="status" data-testid={`featured-state-connected-${p.id}`} className={`${PILL} border-emerald-200 bg-emerald-50 text-emerald-700`}><Check className="h-3.5 w-3.5" /> Connected</span>;
  if (state === "REQUEST_SENT") return <span role="status" data-testid={`featured-state-pending-${p.id}`} className={`${PILL} border-amber-200 bg-amber-50 text-amber-700`}><Clock className="h-3.5 w-3.5" /> Pending</span>;
  if (state === "RESPOND") return <span role="status" title="Respond in Requests" data-testid={`featured-state-respond-${p.id}`} className={`${PILL} border-amber-200 bg-amber-50 text-amber-700`}><Inbox className="h-3.5 w-3.5" /> Respond</span>;
  return (
    <button type="button" data-testid={`featured-connect-btn-${p.id}`} disabled={busy[p.id]} onClick={send} aria-label={`Connect with ${p.name}`}
      className={`${PILL} border-[#0F2C59]/25 bg-[#0F2C59]/[0.04] text-[#0F2C59] transition-colors hover:bg-[#0F2C59]/10 disabled:opacity-60`}>
      {busy[p.id] ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Connect
    </button>
  );
}

function ConnectButton({ p, busy, mark, onChanged, full, tid = "" }) {
  const [state, setState] = useState(p.connection_state);
  useEffect(() => setState(p.connection_state), [p.connection_state]);
  const w = full ? "w-full" : "";

  const send = async () => {
    mark(p.id, true);
    try {
      await api.post("/connections/request", { recipient_id: p.id });
      setState("REQUEST_SENT");
      toast.success(`Connection request sent to ${p.name}`);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { mark(p.id, false); }
  };

  if (state === "CONNECTED") return <Button data-testid={`${tid}state-connected-${p.id}`} disabled variant="outline" className={`${w} rounded-xl text-success border-success/30 bg-success-soft`}><Check className="h-4 w-4 mr-1.5" /> Connected</Button>;
  if (state === "REQUEST_SENT") return <Button data-testid={`${tid}state-pending-${p.id}`} disabled variant="outline" className={`${w} rounded-xl text-slate-500`}><Clock className="h-4 w-4 mr-1.5" /> Request sent</Button>;
  if (state === "RESPOND") return <Button data-testid={`${tid}state-respond-${p.id}`} disabled variant="outline" className={`${w} rounded-xl text-[#C79A3B] border-[#C79A3B]/40`}><Inbox className="h-4 w-4 mr-1.5" /> Respond in Requests</Button>;
  return <Button data-testid={`${tid}connect-btn-${p.id}`} disabled={busy[p.id]} onClick={send} className={`${w} rounded-xl bg-[#0F2C59] hover:bg-[#143163]`}>{busy[p.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1.5" /> Connect</>}</Button>;
}

/* ---------------- My Connections ---------------- */
function ConnectionsTab({ openProfile, onMessage, onRefer }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/connections").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  if (!data) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data.items.length) return <EmptyState testid="connections-empty" icon={Users} title="No connections yet" hint="Head to Discover to find verified professionals and send your first connection request." />;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {data.items.map((p) => (
        <div key={p.id} data-testid={`connection-card-${p.id}`} className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md transition-shadow">
          <ProfileHead p={p} onFallback={() => openProfile(p.id)} testid={`connection-open-${p.id}`}>
            <Avatar name={p.name} url={p.avatar_url} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0"><span className="font-semibold text-[#0F2C59] truncate">{p.name}</span>{p.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}</div>
              <MetaRow p={p} />
            </div>
          </ProfileHead>
          <div className="mt-3 flex items-center gap-2">
            <Button data-testid={`message-${p.id}`} onClick={() => onMessage(p.id)} variant="outline" className="flex-1 rounded-xl border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5"><MessagesSquare className="h-4 w-4 mr-1.5" /> Message</Button>
            <Button data-testid={`refer-client-${p.id}`} onClick={() => onRefer(p)} className="flex-1 rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white"><Handshake className="h-4 w-4 mr-1.5" /> Refer Client</Button>
          </div>
          <div className="mt-2 flex items-center justify-between gap-2">
            <ViewProfile p={p} testid={`connection-view-profile-${p.id}`} />
            <span className="text-success bg-success-soft rounded-full px-2 py-1 text-[11px] font-semibold inline-flex items-center gap-1 shrink-0"><Check className="h-3 w-3" /> Connected</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Requests ---------------- */
function RequestsTab({ busy, mark, onChanged, openProfile }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/connections/requests").then((r) => setData(r.data)).catch(() => setData({ incoming: [], sent: [] })), []);
  useEffect(() => { load(); }, [load]);

  const act = async (p, action) => {
    mark(p.connection_id, true);
    try {
      await api.post(`/connections/${p.connection_id}/${action}`);
      toast.success(action === "accept" ? `You are now connected with ${p.name}` : action === "decline" ? "Request declined" : "Request cancelled");
      await load(); onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { mark(p.connection_id, false); }
  };

  if (!data) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const { incoming = [], sent = [] } = data;

  return (
    <div className="space-y-8">
      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2"><Inbox className="h-4 w-4" /> Incoming ({incoming.length})</h2>
        {incoming.length === 0 ? (
          <EmptyState testid="incoming-empty" icon={Inbox} title="No incoming requests" hint="When a verified professional wants to connect, their request will show up here." />
        ) : (
          <div className="space-y-3">
            {incoming.map((p) => (
              <div key={p.connection_id} data-testid={`incoming-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <ProfileHead p={p} onFallback={() => openProfile(p.id)} testid={`incoming-open-${p.id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                  <Avatar name={p.name} url={p.avatar_url} />
                  <div className="min-w-0"><div className="flex items-center gap-1.5"><span className="font-semibold text-[#0F2C59] truncate">{p.name}</span>{p.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}</div><MetaRow p={p} /><ViewProfile p={p} testid={`incoming-view-profile-${p.id}`} /></div>
                </ProfileHead>
                <div className="flex gap-2 shrink-0">
                  <Button data-testid={`accept-${p.id}`} disabled={busy[p.connection_id]} onClick={() => act(p, "accept")} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163]">{busy[p.connection_id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Accept</>}</Button>
                  <Button data-testid={`decline-${p.id}`} disabled={busy[p.connection_id]} onClick={() => act(p, "decline")} variant="outline" className="rounded-xl"><X className="h-4 w-4 mr-1" /> Decline</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2"><Send className="h-4 w-4" /> Sent ({sent.length})</h2>
        {sent.length === 0 ? (
          <EmptyState testid="sent-empty" icon={Send} title="No pending sent requests" hint="Requests you send from Discover will appear here until they're accepted or declined." />
        ) : (
          <div className="space-y-3">
            {sent.map((p) => (
              <div key={p.connection_id} data-testid={`sent-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3">
                <ProfileHead p={p} onFallback={() => openProfile(p.id)} testid={`sent-open-${p.id}`} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                  <Avatar name={p.name} url={p.avatar_url} />
                  <div className="min-w-0"><div className="flex items-center gap-1.5"><span className="font-semibold text-[#0F2C59] truncate">{p.name}</span>{p.verified && <BadgeCheck className="h-4 w-4 text-success shrink-0" />}</div><MetaRow p={p} /><ViewProfile p={p} testid={`sent-view-profile-${p.id}`} /></div>
                </ProfileHead>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-slate-500 text-[11px] inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Pending</span>
                  <Button data-testid={`cancel-${p.id}`} disabled={busy[p.connection_id]} onClick={() => act(p, "cancel")} variant="outline" className="rounded-xl">Cancel</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* ---------------- Recent Interactions ---------------- */
const KIND_LABEL = {
  request_sent: { icon: Send, text: "You sent a connection request", tone: "text-slate-500" },
  request_received: { icon: Inbox, text: "Sent you a connection request", tone: "text-[#C79A3B]" },
  connection_accepted: { icon: Check, text: "You are connected", tone: "text-success" },
  request_declined: { icon: X, text: "Request declined", tone: "text-slate-400" },
  request_cancelled: { icon: RotateCcw, text: "Request cancelled", tone: "text-slate-400" },
};
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
function InteractionsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/connections/interactions").then((r) => setData(r.data)).catch(() => setData({ items: [] })); }, []);
  if (!data) return <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  if (!data.items.length) return <EmptyState testid="interactions-empty" icon={History} title="No activity yet" hint="Your connection requests, accepts and declines will appear here as a simple, business-only timeline." />;
  return (
    <ul data-testid="interactions-list" className="space-y-2">
      {data.items.map((it, i) => {
        const meta = KIND_LABEL[it.kind] || KIND_LABEL.request_sent;
        const Icon = meta.icon;
        return (
          <li key={i} data-testid={`interaction-${i}`} className="rounded-xl border border-slate-200 bg-white p-3.5 flex items-center gap-3">
            <Avatar name={it.professional?.name} url={it.professional?.avatar_url} size="h-9 w-9" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#0F2C59]"><span className="font-semibold">{it.professional?.name}</span></p>
              <p className={`text-[13px] flex items-center gap-1.5 ${meta.tone}`}><Icon className="h-3.5 w-3.5" /> {meta.text}</p>
            </div>
            <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(it.at)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/* ---------------- Profile Drawer ---------------- */
function ProfileDrawer({ id, onClose, busy, mark, onChanged, onMessage }) {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!id) { setP(null); return; }
    setLoading(true);
    api.get(`/connections/professional/${id}`)
      .then((r) => setP(r.data))
      .catch((e) => { toast.error(apiError(e)); onClose(); })
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="profile-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="sr-only">Professional profile</SheetTitle>
          <SheetDescription className="sr-only">Read-only profile of a verified professional</SheetDescription>
        </SheetHeader>
        {loading || !p ? (
          <div className="flex justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="pt-2">
            <div className="flex items-center gap-4">
              <Avatar name={p.name} url={p.avatar_url} size="h-16 w-16" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5"><h3 className="font-display text-xl font-bold text-[#0F2C59] truncate">{p.name}</h3>{p.verified && <BadgeCheck className="h-5 w-5 text-success shrink-0" />}</div>
                {p.role_label && <p className="text-sm text-slate-500">{p.role_label}</p>}
              </div>
            </div>

            <div className="mt-5 space-y-2.5 text-sm">
              {p.sales_position_label && <div data-testid="drawer-position" className="flex items-center gap-2 text-slate-600"><Handshake className="h-4 w-4 text-slate-400 shrink-0" /> <span className="min-w-0">{p.sales_position_label}</span></div>}
              {p.affiliation_status === "INDEPENDENT" ? (
                <div data-testid="drawer-affiliation" className="flex items-center gap-2 text-slate-600"><Building2 className="h-4 w-4 text-slate-400 shrink-0" /> Independent / Freelance</div>
              ) : (p.organization && (
                <div data-testid="drawer-org" className="flex items-start gap-2 text-slate-600"><Building2 className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" /> <span className="min-w-0 break-words">{p.organization}{p.organization_type_label ? ` · ${p.organization_type_label}` : ""}{p.organization_branch ? ` — ${p.organization_branch}` : ""}</span></div>
              ))}
              {p.location && <div className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4 text-slate-400 shrink-0" /> {p.location}</div>}
              {Array.isArray(p.service_areas) && p.service_areas.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">{p.service_areas.slice(0, 8).map((a, i) => <span key={i} className="rounded-full bg-slate-100 text-slate-600 text-[11px] px-2 py-0.5">{a}</span>)}</div>
              )}
            </div>

            {p.bio ? <p className="mt-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{p.bio}</p> : null}

            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4">
              <div className="rounded-xl bg-slate-50 p-3 text-center"><div className="text-xl font-bold text-[#0F2C59]">{p.active_listing_count ?? 0}</div><div className="text-[11px] text-slate-500 mt-0.5">Active Listings</div></div>
              <div className="rounded-xl bg-slate-50 p-3 text-center"><div className="text-xl font-bold text-[#0F2C59]">{p.active_buyer_requirement_count ?? 0}</div><div className="text-[11px] text-slate-500 mt-0.5">Active Buyer Needs</div></div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              <div className="flex-1"><ConnectButton p={p} busy={busy} mark={mark} onChanged={onChanged} full /></div>
              {p.connection_state === "CONNECTED" && (
                <Button data-testid="drawer-message-btn" onClick={() => onMessage(p.id)} variant="outline" className="rounded-xl border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5"><MessagesSquare className="h-4 w-4 mr-1.5" /> Message</Button>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
